import { Typography } from '@navi/web-ui/lib/primitives';
import cx from 'classnames';
import React, { useEffect } from 'react';
import { formatAmount } from 'src/utils/commonUtils';
import { DateFormat, dateFormat } from 'src/utils/DateHelper';
import { Due } from '../../interfaces/CaseDetail.type';
import styles from './index.module.scss';
import { useSelector } from 'react-redux';
import { RootState } from '@cp/src/store';
import dayjs from 'dayjs';
import HourGlassIcon from '@cp/src/assets/icons/HourGlassIcon';
import { addClickstreamEvent } from '@cp/src/service/clickStreamEventService';
import { FEE_WAIVER_CLICKSTREAM } from '@cp/src/service/clickStream.constant';

interface EmiBreakupRowProps {
  rowData: Due;
}

const EmiBreakupRow: React.FC<EmiBreakupRowProps> = props => {
  const { rowData } = props;
  const featureFlags = useSelector((store: RootState) => store?.common?.featureFlags);

  const { feeWaiverFeatureFlag } = featureFlags || {};

  const showEMIPenaltySummary = React.useMemo(() => {
    const dueAmount = rowData?.totalAmount;
    return dueAmount !== rowData?.paidAmount && !!rowData.referenceId;
  }, [rowData]);

  const { holdAmount, additionalWaiverInfo, waivedAmount } = rowData || {};
  const { heldTillInDateTimeFormat, refundedAmount } = additionalWaiverInfo || {};

  useEffect(() => {
    addClickstreamEvent(FEE_WAIVER_CLICKSTREAM.LH_EMI_BREAKUP_LOADED, {
      referenceId: rowData.referenceId
    });
  }, []);

  return (
    <div className={styles.emiWidget}>
      <div className={styles.emiCalendarRow}>
        <div className={styles.titles}>
          <Typography variant="p3" className="basis-[44%]" color="var(--navi-color-gray-c2)">
            Amount Type
          </Typography>
          <Typography variant="p3" className="basis-[28%]" color="var(--navi-color-gray-c2)">
            Amount
          </Typography>
          <Typography variant="p3" className="basis-[28%]" color="var(--navi-color-gray-c2)">
            Unpaid
          </Typography>
        </div>
        <div className={styles.item}>
          <Typography variant="p3" className="basis-[44%]" color="var(--navi-color-gray-c3)">
            Principal
          </Typography>
          <Typography variant="h4" className="basis-[28%]" color="var(--navi-color-gray-c3)">
            {formatAmount(rowData.principal, false)}
          </Typography>
          <Typography
            variant="h4"
            color="var(--navi-color-gray-c3)"
            className={cx('basis-[28%]', styles[rowData.status])}
          >
            {formatAmount(rowData.unpaidPrincipal, false)}
          </Typography>
        </div>
        <div className={styles.item}>
          <Typography variant="p3" className="basis-[44%]" color="var(--navi-color-gray-c3)">
            Interest
          </Typography>
          <Typography variant="h4" className="basis-[28%]" color="var(--navi-color-gray-c3)">
            {formatAmount(rowData.interest, false)}
          </Typography>
          <Typography
            variant="h4"
            color="var(--navi-color-gray-c3)"
            className={cx('basis-[28%]', styles[rowData.status])}
          >
            {formatAmount(rowData.unpaidInterest, false)}
          </Typography>
        </div>
        {rowData.referenceId ? (
          <div className={styles.item}>
            <Typography variant="p3" className="basis-[44%]" color="var(--navi-color-gray-c3)">
              EMI Penalty Charges
            </Typography>
            <Typography variant="h4" className="basis-[28%]" color="var(--navi-color-gray-c3)">
              {formatAmount(rowData.totalPenalty, false)}
            </Typography>
            <Typography
              variant="h4"
              color="var(--navi-color-gray-c3)"
              className={cx('basis-[28%]', styles[rowData.status])}
            >
              {formatAmount(rowData.unpaidPenalty, false)}
            </Typography>
          </div>
        ) : null}
      </div>
      {feeWaiverFeatureFlag ? (
        <div className={styles.emiSummarySection}>
          <div className={styles.titles}>
            <Typography variant="p3">
              EMI Penalty Charges Summary ({formatAmount(rowData.totalPenalty, false)})
            </Typography>
          </div>
          {holdAmount ? (
            <div className="flex">
              <Typography
                variant="p3"
                className={styles.waiverAmount}
                color="var(--navi-color-gray-c3)"
              >
                Hold amount
                {heldTillInDateTimeFormat ? (
                  <div className="flex items-center mt-1">
                    <div className="mr-1">
                      <HourGlassIcon />
                    </div>
                    <Typography variant="p5" color="var(--grayscale-3)">
                      (till {dayjs(heldTillInDateTimeFormat).format(DateFormat.HH_mm_a)} today)
                    </Typography>
                  </div>
                ) : null}
              </Typography>
              <Typography variant="h4" color="var(--navi-color-gray-c3)">
                {formatAmount(holdAmount || 0, false)}
              </Typography>
            </div>
          ) : null}
          {waivedAmount || !holdAmount ? (
            <div className="flex">
              <Typography
                variant="p3"
                className={styles.waiverAmount}
                color="var(--navi-color-gray-c3)"
              >
                Waived amount
              </Typography>
              <Typography variant="h4" color="var(--navi-color-gray-c3)" className="ml-3">
                {formatAmount(waivedAmount || 0, false)}
              </Typography>
            </div>
          ) : null}
          {refundedAmount > 0 ? (
            <div className="flex">
              <Typography
                variant="p3"
                className={styles.waiverAmount}
                color="var(--navi-color-gray-c3)"
              >
                To be refunded
              </Typography>
              <Typography variant="h4" color="var(--navi-color-gray-c3)">
                {formatAmount(refundedAmount, false)}
              </Typography>
            </div>
          ) : null}
        </div>
      ) : showEMIPenaltySummary ? (
        <div className={styles.emiSummarySection}>
          <div className={styles.titles}>
            <Typography variant="p3">
              EMI Penalty Charges Summary ({formatAmount(rowData.totalPenalty, false)})
            </Typography>
          </div>
          <div className={styles.item}>
            <Typography variant="p3" className={styles.waiverAmount}>
              Waived amount
            </Typography>
            <Typography variant="h4">
              {rowData?.waivedAmount ? formatAmount(rowData.waivedAmount, false) : '-'}
            </Typography>
          </div>
          <div className={styles.item}>
            <Typography variant="p3" className={styles.waiverAmount}>
              Hold amount
            </Typography>
            <Typography variant="h4">
              {rowData?.holdAmount ? formatAmount(rowData.holdAmount, false) : '-'}
            </Typography>
          </div>
          <Typography variant="p5" className={styles.timeStamp}>
            {rowData.heldTill
              ? `(Till ${dateFormat(new Date(rowData.heldTill), "ddMMM 'YY")} 11:59 PM)`
              : null}
          </Typography>
        </div>
      ) : null}
    </div>
  );
};

export default EmiBreakupRow;
